import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { ArrowLeft, Link as LinkIcon, Download, Loader2, ExternalLink } from 'lucide-preact'
import { WebsterBotStatusBadge } from './WebsterBotStatusBadge'
import { humanizeSubCode } from './websterStatus'
import { MeetingRecordingPlayer } from './MeetingRecordingPlayer'
import { MeetingTranscriptPanel } from './MeetingTranscriptPanel'
import { useMeetingTranscript } from '@/hooks/useMeetingTranscript'
import { activeLineIndex, type TranscriptLine } from '@/utils/meetingTranscript'
import { useTimeZones } from '@/components/calendar/hooks/useTimeZones'
import { formatEventDateTime } from '@/utils/dateTimeFormatters'
import { normalizeMeetingUrl } from '@/utils/meetingUrl'
import type { TouchPointViewDto } from '@/types/touchPoint'

interface Props {
  touchPoint: TouchPointViewDto
  workspaceId: string
  onClose: () => void
}

/**
 * Full-page meeting view (ORB-436) — the routed overlay that replaces the read-only slide-in for a
 * Webster-recorded meeting: recording + details on the left, a playback-synced transcript on the right.
 * Rendered fixed over the (still-mounted) Meetings tab so closing snaps back to the list, while the URL
 * (/.../meeting/:id) keeps it deep-linkable, refresh-safe, and Back-button closable.
 *
 * Coordinates the two panes: the video's timeupdate advances the highlighted transcript line, and
 * clicking a line seeks the video. Times come from formatEventDateTime (the shared wall-clock helper the
 * list uses) so they never re-shift into the viewer's local zone.
 */
export function MeetingPage({ touchPoint, workspaceId, onClose }: Props) {
  const { getAbbreviation } = useTimeZones()

  // Escape closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const meeting = touchPoint.MeetingProperties
  const tzName = meeting?.TimeZone ? getAbbreviation(meeting.TimeZone, meeting.StartTime) : ''
  const botStatus = meeting?.BotStatus
  const botSubCode = meeting?.BotStatusSubCode
  const showBotStatus = !!botStatus && botStatus !== 'Not Scheduled'
  const recordingReady = meeting?.HasRecording || botStatus === 'Done'
  const hasTranscript = !!meeting?.TranscriptJsonPath

  const { state: transcriptState, lines, url: transcriptRawUrl } = useMeetingTranscript(workspaceId, touchPoint.Id, hasTranscript)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [activeIndex, setActiveIndex] = useState(-1)

  // Read the latest lines from a ref so the video's timeupdate handler stays stable, and only re-render
  // when the active line actually changes (timeupdate fires several times a second).
  const linesRef = useRef<TranscriptLine[]>([])
  useEffect(() => { linesRef.current = lines }, [lines])
  const handleTimeUpdate = useCallback((currentTimeSeconds: number) => {
    const index = activeLineIndex(linesRef.current, currentTimeSeconds)
    setActiveIndex(prev => (prev === index ? prev : index))
  }, [])

  const seekTo = useCallback((seconds: number) => {
    const video = videoRef.current
    if (!video || !Number.isFinite(seconds)) return
    try { video.currentTime = Math.max(0, seconds) } catch { /* seeking before metadata loads — ignore */ }
    void video.play?.()?.catch?.(() => { /* autoplay policy blocked play — leave paused at the seek */ })
  }, [])

  const participants = useMemo(() => Array.from(new Set(lines.map(line => line.speaker))), [lines])

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-900"
      aria-modal="true"
      role="dialog"
      aria-labelledby="meeting-page-title"
    >
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 sm:px-6 h-14 shrink-0 border-b border-slate-200 dark:border-slate-700">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md px-1.5 py-1 -ml-1.5"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
        <h1 id="meeting-page-title" className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white truncate">
          {touchPoint.Name}
        </h1>
        {showBotStatus && (
          <div className="ml-auto shrink-0">
            <WebsterBotStatusBadge status={botStatus} subCode={botSubCode} />
          </div>
        )}
      </div>

      {/* Two-pane body: video + details left, transcript right. Below lg the panes stack and the whole
          body scrolls; at lg+ each pane manages its own height and the page itself doesn't scroll. */}
      <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* Left: video hero + details */}
        <div className="lg:h-full lg:overflow-y-auto p-4 sm:p-6 space-y-5 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-slate-700">
          {recordingReady ? (
            <MeetingRecordingPlayer
              workspaceId={workspaceId}
              touchPointId={touchPoint.Id}
              autoLoad
              videoRef={videoRef}
              onTimeUpdate={handleTimeUpdate}
            />
          ) : (
            <div className="aspect-video w-full rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-center px-6">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {botStatus === 'Failed' && botSubCode
                  ? humanizeSubCode(botSubCode)
                  : 'The recording will appear here once Webster finishes processing.'}
              </p>
            </div>
          )}

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
            {meeting?.Type && (
              <div>
                <dt className="font-medium text-slate-500 dark:text-slate-400">Type</dt>
                <dd className="mt-0.5 text-slate-900 dark:text-slate-100">{meeting.Type}</dd>
              </div>
            )}
            {tzName && (
              <div>
                <dt className="font-medium text-slate-500 dark:text-slate-400">Time zone</dt>
                <dd className="mt-0.5 text-slate-900 dark:text-slate-100">{tzName}</dd>
              </div>
            )}
            {meeting?.StartTime && (
              <div>
                <dt className="font-medium text-slate-500 dark:text-slate-400">Start</dt>
                <dd className="mt-0.5 text-slate-900 dark:text-slate-100">
                  {formatEventDateTime(meeting.StartTime, { includeDate: true, includeTime: true, timezoneName: tzName })}
                </dd>
              </div>
            )}
            {meeting?.EndTime && (
              <div>
                <dt className="font-medium text-slate-500 dark:text-slate-400">End</dt>
                <dd className="mt-0.5 text-slate-900 dark:text-slate-100">
                  {formatEventDateTime(meeting.EndTime, { includeDate: true, includeTime: true, timezoneName: tzName })}
                </dd>
              </div>
            )}
            {participants.length > 0 && (
              <div className="sm:col-span-2">
                <dt className="font-medium text-slate-500 dark:text-slate-400">Participants</dt>
                <dd className="mt-0.5 text-slate-900 dark:text-slate-100">{participants.join(', ')}</dd>
              </div>
            )}
            {meeting?.MeetingUrlLink && (
              <div className="sm:col-span-2">
                <dt className="font-medium text-slate-500 dark:text-slate-400">Meeting link</dt>
                <dd className="mt-0.5">
                  <a
                    href={normalizeMeetingUrl(meeting.MeetingUrlLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:underline break-all"
                  >
                    <LinkIcon className="w-3.5 h-3.5 shrink-0" />
                    {meeting.MeetingUrlLink}
                  </a>
                </dd>
              </div>
            )}
            {meeting?.Attachment && (
              <div className="sm:col-span-2">
                <dt className="font-medium text-slate-500 dark:text-slate-400">Attachment</dt>
                <dd className="mt-0.5">
                  <a
                    href={meeting.Attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <Download className="w-4 h-4" /> Download attachment
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Right: transcript */}
        <div className="lg:h-full lg:min-h-0 p-4 sm:p-6 flex flex-col">
          <h2 className="text-[13px] font-semibold text-slate-700 dark:text-slate-200 mb-3 shrink-0">Transcript</h2>
          {transcriptState === 'ready' ? (
            <MeetingTranscriptPanel lines={lines} activeIndex={activeIndex} onSeek={seekTo} rawUrl={transcriptRawUrl || undefined} />
          ) : transcriptState === 'loading' ? (
            <div className="flex items-center gap-2 text-[12.5px] text-slate-500 dark:text-slate-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading transcript…
            </div>
          ) : transcriptState === 'error' || transcriptState === 'unreadable' ? (
            <p className="text-[12.5px] text-slate-500 dark:text-slate-400">
              {transcriptState === 'error'
                ? 'Couldn’t load the transcript here. '
                : 'This transcript isn’t in a readable format. '}
              {transcriptRawUrl && (
                <a
                  href={transcriptRawUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Open raw JSON <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </p>
          ) : (
            <p className="text-[12.5px] text-slate-500 dark:text-slate-400">
              The transcript will appear here once Webster finishes processing.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
